import { useQuery } from '@tanstack/react-query';
import { USE_MOCK, analyticsApi, ApiError, type AnalyticsFilterParams } from '@/api/client';
import {
  MOCK_RESULTS,
  MOCK_PEOPLE,
} from '@/data/mockAnalytics';
import type {
  AnalyticsSummaryResponse,
  FundamentalsResponse,
  HeatmapResponse,
  OnaResponse,
  GenerationalResponse,
  GenerationalParticipantsResponse,
  BiasResponse,
  OmissionsResponse,
  PersonalResponse,
} from '@/api/types';

/** Calls fn and returns fallback on 404 or network errors (service down). Re-throws other errors. */
async function safeCall<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch (err: unknown) {
    if (err instanceof ApiError && err.status === 404) return fallback;
    if (err instanceof TypeError) return fallback; // ERR_CONNECTION_REFUSED / Failed to fetch
    throw err;
  }
}

// ─── Derived summary from mock results ────────────────────────────────────────

function buildMockSummary(): AnalyticsSummaryResponse {
  const numeric = MOCK_RESULTS.filter(r => typeof r.score === 'number');
  const avgScore = numeric.length > 0
    ? +(numeric.reduce((s, r) => s + (r.score as number), 0) / numeric.length).toFixed(2)
    : 0;
  const noObs = MOCK_RESULTS.filter(r => r.score === 'no_obs').length;
  const responseRate = MOCK_RESULTS.length > 0
    ? Math.round(((MOCK_RESULTS.length - noObs) / MOCK_RESULTS.length) * 100)
    : 0;

  const byPerson = new Map<string, number[]>();
  for (const r of numeric) {
    const arr = byPerson.get(r.evaluatedId) ?? [];
    arr.push(r.score as number);
    byPerson.set(r.evaluatedId, arr);
  }

  const scoredPeople = [...byPerson.entries()].map(([id, scores]) => {
    const person = MOCK_PEOPLE.find(p => p.id === id);
    return { name: person?.name ?? id, score: +(scores.reduce((a, b) => a + b) / scores.length).toFixed(2) };
  }).sort((a, b) => b.score - a.score);

  return {
    avgScore,
    totalEvaluations: MOCK_RESULTS.length,
    responseRate,
    avgPerDay: +(MOCK_RESULTS.length / 30).toFixed(1),
    uniqueEvaluators: new Set(MOCK_RESULTS.map(r => r.evaluatorId)).size,
    topBehaviors: scoredPeople.slice(0, 3).map(p => ({ name: p.name, score: p.score })),
    opportunities: scoredPeople.slice(-3).reverse().map(p => ({ name: p.name, score: p.score })),
    omissionsByUnit: [],
    scoreByOu: [],
  };
}

const EMPTY_SUMMARY: AnalyticsSummaryResponse = {
  avgScore: 0, totalEvaluations: 0, responseRate: 0, avgPerDay: 0,
  uniqueEvaluators: 0, topBehaviors: [], opportunities: [], omissionsByUnit: [],
  scoreByOu: [],
};

// ─── Hooks ────────────────────────────────────────────────────────────────────

export function useAnalyticsSummary(filters: AnalyticsFilterParams = {}) {
  return useQuery<AnalyticsSummaryResponse>({
    queryKey: ['analytics', 'summary', filters],
    retry: false,
    queryFn: () => USE_MOCK
      ? Promise.resolve(buildMockSummary())
      : safeCall(() => analyticsApi.getSummary(filters), EMPTY_SUMMARY),
  });
}

export function useAnalyticsFundamentals(filters: AnalyticsFilterParams = {}) {
  return useQuery<FundamentalsResponse>({
    queryKey: ['analytics', 'fundamentals', filters],
    retry: false,
    queryFn: (): Promise<FundamentalsResponse> => USE_MOCK
      ? Promise.resolve([] as FundamentalsResponse)
      : safeCall(() => analyticsApi.getFundamentals(filters), [] as FundamentalsResponse),
  });
}

export function useAnalyticsHeatmap(filters: AnalyticsFilterParams = {}) {
  return useQuery<HeatmapResponse>({
    queryKey: ['analytics', 'heatmap', filters],
    retry: false,
    queryFn: () => USE_MOCK
      ? Promise.resolve({ fundamentalIds: [], fundamentalNames: [], rows: [] } as HeatmapResponse)
      : safeCall(() => analyticsApi.getHeatmap(filters), { fundamentalIds: [], fundamentalNames: [], rows: [] } as HeatmapResponse),
  });
}

const EMPTY_ONA: OnaResponse = { edges: [] };

export function useAnalyticsOna(filters: AnalyticsFilterParams = {}) {
  return useQuery<OnaResponse>({
    queryKey: ['analytics', 'ona', filters],
    retry: false,
    queryFn: async () => {
      if (USE_MOCK) {
        return {
          edges: MOCK_PEOPLE.slice(1).map((person, index) => ({
            evaluatorId: MOCK_PEOPLE[index]!.id,
            evaluatedId: person.id,
            interactionCount: 5,
          })),
        };
      }
      return safeCall(() => analyticsApi.getOna(filters), EMPTY_ONA);
    },
  });
}

export function useAnalyticsGenerational(filters: AnalyticsFilterParams = {}) {
  return useQuery<GenerationalResponse>({
    queryKey: ['analytics', 'generational', filters],
    retry: false,
    queryFn: (): Promise<GenerationalResponse> => USE_MOCK
      ? Promise.resolve([] as GenerationalResponse)
      : safeCall(() => analyticsApi.getGenerational(filters), [] as GenerationalResponse),
  });
}

export function useAnalyticsGenerationalParticipants(filters: AnalyticsFilterParams = {}) {
  return useQuery<GenerationalParticipantsResponse>({
    queryKey: ['analytics', 'generational-participants', filters],
    retry: false,
    queryFn: (): Promise<GenerationalParticipantsResponse> => USE_MOCK
      ? Promise.resolve([] as GenerationalParticipantsResponse)
      : safeCall(() => analyticsApi.getGenerationalParticipants(filters), [] as GenerationalParticipantsResponse),
  });
}

export function useAnalyticsBias(filters: AnalyticsFilterParams = {}) {
  return useQuery<BiasResponse>({
    queryKey: ['analytics', 'bias', filters],
    retry: false,
    queryFn: () => USE_MOCK
      ? Promise.resolve([] as BiasResponse)
      : safeCall(() => analyticsApi.getBias(filters), [] as BiasResponse),
  });
}

const EMPTY_OMISSIONS: OmissionsResponse = {
  totalParticipants: 0,
  participantsWithZeroResponses: 0,
  participants: [],
};

export function useAnalyticsOmissions(filters: AnalyticsFilterParams = {}) {
  return useQuery<OmissionsResponse>({
    queryKey: ['analytics', 'omissions', filters],
    retry: false,
    queryFn: () => USE_MOCK
      ? Promise.resolve(EMPTY_OMISSIONS)
      : safeCall(() => analyticsApi.getOmissions(filters), EMPTY_OMISSIONS),
  });
}

export function useAnalyticsPersonal(userId?: string) {
  return useQuery<PersonalResponse>({
    queryKey: ['analytics', 'personal', userId],
    retry: false,
    queryFn: (): Promise<PersonalResponse> => {
      if (USE_MOCK) {
        return Promise.resolve({ avgScore: 3.4, trend: 0.2, fundamentalScores: [] as PersonalResponse['fundamentalScores'], recentSurveys: [] });
      }
      return safeCall(() => analyticsApi.getPersonal(userId), { avgScore: 0, trend: 0, fundamentalScores: [], recentSurveys: [] });
    },
  });
}
